
import React, { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Message } from "@/hooks/medical-assistant/types";
import { ImagePreview } from "./chat-input/ImagePreview";
import { VoiceControls } from "./chat-input/VoiceControls";
import { ImageUploadButton } from "./chat-input/ImageUploadButton";
import { SubmitButton } from "./chat-input/SubmitButton";

interface ChatInputAreaProps {
  input: string;
  setInput: (input: string) => void;
  handleFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  isRecording?: boolean;
  isProcessingAudio?: boolean;
  isSpeaking?: boolean;
  startRecording?: () => void;
  stopRecording?: () => void;
  stopRecordingAndSubmit?: () => void;
  saveVoiceRecording?: (audioUrl: string) => void;
  selectedImage?: File | null;
  previewUrl?: string | null;
  clearImage?: () => void;
  handleImageSelect?: () => void;
  handleImageChange?: (file: File | null) => void;
  currentLanguage?: string;
  messages?: Message[];
  modalOpen?: boolean;
  toggleVoiceModal?: (open: boolean) => void;
  continuousMode?: boolean;
  autoVoiceResponse?: boolean;
  voiceOnlyMode?: boolean;
  volumeLevel?: number;
}

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  input,
  setInput,
  handleFormSubmit,
  isLoading,
  isRecording,
  isProcessingAudio,
  isSpeaking,
  startRecording,
  stopRecording,
  stopRecordingAndSubmit,
  selectedImage,
  previewUrl,
  clearImage,
  handleImageChange,
}) => {
  const [hasVoiceInput, setHasVoiceInput] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && !isProcessingAudio && (input.trim() !== '' || selectedImage || hasVoiceInput || isRecording)) {
        const form = e.currentTarget.closest('form');
        if (form) form.requestSubmit();
      }
    }
  };

  const handleStartRecording = () => {
    if (startRecording) {
      startRecording();
      setHasVoiceInput(false);
    }
  };

  const handleStopRecording = () => {
    if (stopRecording) {
      stopRecording();
      setHasVoiceInput(true);
    }
  };

  const handleFormSubmitWrapper = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isRecording && stopRecordingAndSubmit) {
      console.log("Recording in progress, calling stopRecordingAndSubmit");
      stopRecordingAndSubmit();
      return;
    }
    
    console.log("Normal form submission");
    handleFormSubmit(e);
    setHasVoiceInput(false);
  };

  const isSubmitDisabled = isLoading || isProcessingAudio || (input.trim() === '' && !selectedImage && !hasVoiceInput && !isRecording);

  return (
    <div className="border-t border-blue-100 p-4 bg-white/60">
      {previewUrl && clearImage && (
        <ImagePreview previewUrl={previewUrl} onClear={clearImage} />
      )}
      
      <form onSubmit={handleFormSubmitWrapper} className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Textarea
              name="message"
              placeholder={selectedImage 
                ? "Posez une question spécifique sur cette image ou laissez vide pour une analyse générale..." 
                : isRecording 
                  ? "🔴 Enregistrement en cours... Cliquez sur Envoyer pour traiter automatiquement" 
                  : hasVoiceInput
                    ? "Message vocal enregistré - cliquez sur Envoyer ou ajoutez du texte..."
                    : isProcessingAudio
                      ? "Traitement de l'audio en cours..."
                      : "Posez votre question médicale ou téléchargez une image..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || isProcessingAudio}
              className="min-h-[80px] resize-none border-blue-200 pr-10 focus:border-blue-400"
            />
            
            {handleImageChange && (
              <ImageUploadButton
                isLoading={isLoading || false}
                isRecording={isRecording || false}
                isProcessingAudio={isProcessingAudio || false}
                onImageChange={handleImageChange}
              />
            )}
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          {startRecording && stopRecording && (
            <VoiceControls
              isRecording={isRecording || false}
              isLoading={isLoading}
              isSpeaking={isSpeaking || false}
              isProcessingAudio={isProcessingAudio || false}
              hasVoiceInput={hasVoiceInput}
              onStartRecording={handleStartRecording}
              onStopRecording={handleStopRecording}
            />
          )}
          
          <SubmitButton
            isLoading={isLoading}
            isProcessingAudio={isProcessingAudio || false}
            isRecording={isRecording || false}
            isDisabled={isSubmitDisabled}
          />
        </div>
      </form>
    </div>
  );
};
